/**
 * SocialCapital.cc — long-form article content for every nav tab.
 *
 * Structure:
 *   articles[slug] = {
 *     eyebrow, title, tagline?, colouredTitle?, blocks: [ Block, ... ],
 *     cta?: { label, to },
 *   }
 *
 * Block types (rendered by SocialCapitalArticle.jsx):
 *   { t: "p",       text }               — paragraph
 *   { t: "lead",    text }               — larger intro paragraph
 *   { t: "h2",      text }               — section heading
 *   { t: "h3",      text }               — subheading
 *   { t: "ul",      items: [text,...] }  — bullet list
 *   { t: "callout", text }               — highlighted quote/callout
 *   { t: "steps",   items: [{n,label,body}] } — numbered process steps
 *   { t: "grid",    columns, items: [{title,body}] } — 2/3-column card grid
 *   { t: "divider" }                     — small separator
 */

export const articles = {
  // ────────────────────────────────────────────────────────────
  "what-is": {
    eyebrow: "What Is Social Capital?",
    title: "The Value We Create Through Relationships, Trust and Contribution",
    tagline:
      "Social capital is not a new concept. SocialCapital.cc brings it into the digital age.",
    blocks: [
      { t: "p", text: "Researchers in sociology, economics, education, public health and community development have long studied the value created by relationships, networks, trust, cooperation and participation." },
      { t: "p", text: "A community where people know one another, help each other, share knowledge and work toward common goals possesses forms of social capital." },
      { t: "p", text: "SocialCapital.cc brings this concept into the digital age." },

      { t: "h2", text: "Three foundations of social capital" },
      { t: "grid", columns: 3, items: [
        { title: "Relationships", body: "Human beings accomplish more through cooperation. Relationships between neighbours, students, teachers, coworkers, volunteers and organisations create networks through which knowledge, assistance and opportunities can flow." },
        { title: "Trust",         body: "Healthy communities depend on trust. Trust develops through consistent behaviour, reliability, cooperation and accountability. SocialCapital.cc therefore places special importance on verification and evidence rather than relying only on self-reported claims." },
        { title: "Contribution",  body: "Relationships become especially valuable when they lead to positive action. Teaching a skill, volunteering at a food bank, mentoring a student, planting trees or sharing useful knowledge are examples of activities that can strengthen communities." },
      ]},

      { t: "h2", text: "Social capital in the digital world" },
      { t: "p", text: "Traditional social media primarily measures attention." },
      { t: "ul", items: [
        "How many followers?",
        "How many views?",
        "How many likes?",
        "How many shares?",
      ]},
      { t: "p", text: "These numbers may tell us something about popularity, but they tell us very little about someone's contribution to society." },
      { t: "p", text: "SocialCapital.cc asks different questions:" },
      { t: "ul", items: [
        "What did you contribute?",
        "Whom did you help?",
        "What did you improve?",
        "What knowledge did you share?",
        "What impact did you create?",
        "Can the contribution be documented or verified?",
      ]},

      { t: "h2", text: "Social capital is not human worth" },
      { t: "callout", text: "A person with little recorded Social Capital is not less valuable than someone with a large record. SocialCapital.cc measures documented participation within its system. It does not measure the value of a human being." },
      { t: "p", text: "Someone may be new to the platform, may choose to keep activities private, may live where participating organisations are unavailable or may simply choose not to document their contributions." },

      { t: "h2", text: "Building a culture of contribution" },
      { t: "p", text: "Our larger goal is a simple cultural shift — from “Look at me” toward “Look at what we accomplished.”" },
      { t: "p", text: "Communities become stronger when people contribute knowledge, time, skills, compassion and effort. When those contributions are documented over many years, they can become something powerful: a reputation built on action." },
    ],
    cta: { label: "See how it works", to: "/sc/how-it-works" },
  },

  // ────────────────────────────────────────────────────────────
  "how-it-works": {
    eyebrow: "How It Works",
    title: "From a Good Deed to a Lifetime Record",
    tagline: "Building Social Capital should be simple. Six basic steps.",
    blocks: [
      { t: "steps", items: [
        { n: 1, label: "Do",                 body: "Do something that creates positive value. Volunteer. Mentor. Teach. Plant. Donate. Document. Help. Organise. Research. Preserve. Participate. The activity does not have to be extraordinary — everyday contributions matter." },
        { n: 2, label: "Document",           body: "Create an activity record: what you did, when you did it, how much time you contributed, the organisation involved, the project, the community, photographs, videos, documents, links, and measurable results." },
        { n: 3, label: "Connect",            body: "Connect the contribution with the appropriate people, organisations or communities — a school, university, nonprofit, business, city, community, event, environmental project, food bank, alumni organisation, or Social Pulse® network site." },
        { n: 4, label: "Verify",             body: "Not every activity requires the same degree of verification. SocialCapital.cc distinguishes between activities that are Self-Documented, Evidence-Supported, Peer-Confirmed, Organisation-Verified, Institution-Verified, Platform-Verified or Multi-Source Verified. The stronger the evidence, the greater the confidence in the record." },
        { n: 5, label: "Measure & Recognise", body: "The Social Capital Engine evaluates qualifying contributions. Factors include effort, time, impact, quality, verification, consistency, community benefit and trust. Recognition may include Social Capital points, category achievements, milestones and badges." },
        { n: 6, label: "Build",              body: "Each qualifying activity becomes another chapter in your Social Capital history. One activity becomes ten. Ten become one hundred. Months become years. Eventually your Social Capital Timeline can tell the story of decades of contribution." },
      ]},

      { t: "callout", text: "Do good. Document it. Build your record." },
    ],
    cta: { label: "Explore ways to earn", to: "/sc/earn" },
  },

  // ────────────────────────────────────────────────────────────
  "earn": {
    eyebrow: "Earn",
    title: "There Are Thousands of Ways to Create Social Capital",
    tagline:
      "Social Capital cannot be purchased. It is built through meaningful participation.",
    blocks: [
      { t: "p", text: "Different people contribute in different ways, which is why SocialCapital.cc recognises many categories of activity." },

      { t: "grid", columns: 3, items: [
        { title: "Community Service",         body: "Neighbourhood improvement, community programmes, public projects and local organisations." },
        { title: "Volunteering",              body: "Give your time to nonprofits, schools, hospitals, charities, food banks, cultural organisations and community programmes." },
        { title: "Education",                 body: "Teach, tutor, create educational resources or help another person learn." },
        { title: "Mentoring",                 body: "Guide students, young professionals, entrepreneurs or others who can benefit from your experience." },
        { title: "Environmental Stewardship", body: "Plant trees, restore habitat, participate in cleanups, conserve resources or help environmental organisations." },
        { title: "Citizen Science",           body: "Document plants, wildlife, weather, water and environmental conditions that may contribute to scientific knowledge." },
        { title: "Food & Hunger Relief",      body: "Donate food, rescue surplus food, volunteer at food banks or participate in programmes addressing food insecurity." },
        { title: "Knowledge Sharing",         body: "Publish useful information, create guides, translate educational materials, answer community questions or contribute research." },
        { title: "Culture & Heritage",        body: "Preserve traditions, oral histories, photographs, local history, family history and cultural knowledge." },
        { title: "Elder Assistance",          body: "Help older members of the community with transportation, technology, companionship or everyday needs." },
        { title: "Youth Assistance",          body: "Tutor, coach, mentor and support young people." },
        { title: "Community Safety",          body: "Document hazards and share information about road conditions, weather events, flooding, fallen trees or other community concerns." },
      ]},

      { t: "h2", text: "Acts of kindness" },
      { t: "p", text: "Not every meaningful contribution belongs to an organisation. Helping another person can matter enormously." },

      { t: "h2", text: "Consistency matters" },
      { t: "p", text: "One spectacular deed can create impact. But so can hundreds of small deeds performed quietly over many years. SocialCapital.cc therefore recognises not only individual activities but also consistency and sustained contribution." },
      { t: "callout", text: "The objective is not to chase points. The objective is to create value. The Social Capital follows." },
    ],
    cta: { label: "See your passport", to: "/sc/me" },
  },

  // ────────────────────────────────────────────────────────────
  "passport": {
    eyebrow: "Passport",
    title: "Your Contributions. One Portable Record.",
    tagline:
      "A verified record of the positive things you have accomplished throughout your life.",
    blocks: [
      { t: "p", text: "A traditional résumé describes education and employment. Your Social Capital Passport can complement it by showing how you contribute." },

      { t: "h2", text: "Your passport may include" },
      { t: "ul", items: [
        "Verified activities",
        "Volunteer hours",
        "Community service",
        "Mentoring",
        "Leadership",
        "Environmental contributions",
        "Citizen science",
        "Knowledge sharing",
        "Organisations supported",
        "Projects",
        "Badges",
        "Milestones",
        "Recommendations",
        "Measurable impact",
      ]},

      { t: "h2", text: "Different passports for different purposes" },
      { t: "grid", columns: 3, items: [
        { title: "Student Passport",             body: "Education, activities, service and leadership." },
        { title: "Volunteer Passport",           body: "Organisations, service hours and projects." },
        { title: "Career Passport",              body: "Leadership, mentoring, skills and community involvement." },
        { title: "Environmental Passport",       body: "Conservation, citizen science and environmental activities." },
        { title: "Community Leadership Passport", body: "Civic and community participation." },
        { title: "Legacy Passport",              body: "Selected lifetime contributions." },
      ]},

      { t: "h2", text: "You control what you share" },
      { t: "p", text: "Your entire Social Capital record does not need to be public. You choose which information appears in a Passport. Passports may be shared through secure links or QR codes." },
      { t: "p", text: "Verification allows the recipient to confirm that selected accomplishments originate from the Social Capital system." },

      { t: "h2", text: "Take your reputation with you" },
      { t: "callout", text: "People move. Jobs change. Schools change. Organisations change. Websites disappear. Your contributions should not have to start from zero every time. The Social Capital Passport is designed to travel with you." },
    ],
    cta: { label: "Open my passport", to: "/sc/me" },
  },

  // ────────────────────────────────────────────────────────────
  "students": {
    eyebrow: "Students",
    title: "Build Your Reputation Before You Build Your Résumé",
    tagline:
      "Young people accomplish far more than conventional academic records reveal.",
    blocks: [
      { t: "p", text: "A student may tutor classmates, participate in a school garden, volunteer at a food bank, document wildlife, help organise events, participate in student government, mentor younger students, conduct research, participate in sports, preserve local history, or lead a community project." },
      { t: "p", text: "These experiences demonstrate skills and character that grades alone cannot capture." },

      { t: "h2", text: "Create a Student Social Capital record" },
      { t: "p", text: "Students can document qualifying activities throughout their school years. With appropriate verification, the record can demonstrate service, leadership, initiative, teamwork, environmental responsibility, mentoring, community involvement and consistency." },

      { t: "h2", text: "Social Capital Student Transcript" },
      { t: "p", text: "Over time, participating students may build a Social Capital Student Transcript containing selected verified achievements. This can complement traditional educational records." },
      { t: "p", text: "Students may choose to share it when seeking:" },
      { t: "ul", items: [
        "College admission",
        "Scholarships",
        "Internships",
        "Employment",
        "Leadership programmes",
        "Volunteer opportunities",
      ]},

      { t: "h2", text: "Learn by contributing" },
      { t: "p", text: "Schools can integrate Social Capital with service learning. Students can participate in projects involving nature, agriculture, food waste, community history, elder assistance, citizen science, school improvement and mentoring. Learning becomes connected with real-world contribution." },

      { t: "h2", text: "Protecting young people" },
      { t: "callout", text: "Student participation must be designed around privacy and safety. Minor profiles receive enhanced protections, appropriate parental or institutional permissions and strong controls over personal and location information. Children should be encouraged to contribute — not pressured to compete." },
    ],
    cta: { label: "How verification works", to: "/sc/how-it-works" },
  },

  // ────────────────────────────────────────────────────────────
  "careers": {
    eyebrow: "Careers",
    title: "Your Résumé Shows Where You Worked. Your Social Capital Shows How You Contributed.",
    tagline:
      "Education and experience matter — but they do not tell the whole story.",
    blocks: [
      { t: "p", text: "Employers also value leadership, initiative, reliability, teamwork, communication, mentoring, problem solving, community involvement and willingness to contribute. These qualities can be difficult to demonstrate on a traditional résumé." },

      { t: "h2", text: "Build a Career Social Capital portfolio" },
      { t: "ul", items: [
        "Professional mentoring",
        "Volunteer leadership",
        "Community projects",
        "Skills training",
        "Industry education",
        "Nonprofit participation",
        "Knowledge sharing",
        "Environmental activities",
        "Other verified accomplishments",
      ]},

      { t: "h2", text: "Show evidence, not just claims" },
      { t: "p", text: "Rather than simply writing “Strong community leader”, you might be able to demonstrate:" },
      { t: "callout", text: "Community Mentor — 84 verified hours — 17 participants — 2 organisations." },
      { t: "p", text: "That provides meaningful context." },

      { t: "h2", text: "A complement, not a replacement" },
      { t: "p", text: "Social Capital should complement traditional qualifications. It should never replace education, professional credentials, experience, interviews, references or individualised hiring decisions." },
      { t: "p", text: "Employers should look at the accomplishments behind the Social Capital record rather than simply comparing scores. The objective is to help people show another dimension of who they are: how they contribute." },
    ],
    cta: { label: "Open my passport", to: "/sc/me" },
  },

  // ────────────────────────────────────────────────────────────
  "organizations": {
    eyebrow: "Organisations",
    title: "Measure the Good Your Organisation Helps Create",
    tagline:
      "Schools, nonprofits, businesses, universities, municipalities, food banks and environmental groups all generate tremendous social value.",
    blocks: [
      { t: "p", text: "Yet much of this contribution remains fragmented across spreadsheets, forms, certificates and annual reports. SocialCapital.cc provides a common infrastructure for documenting it." },

      { t: "h2", text: "Social Capital Partner Organisations" },
      { t: "p", text: "Organisations can participate as verified Social Capital partners. Depending on permissions, partners can:" },
      { t: "ul", items: [
        "Create activities",
        "Verify contributions",
        "Manage volunteer programmes",
        "Create challenges",
        "Recognise participants",
        "Award approved badges",
        "Measure impact",
        "Produce reports",
      ]},

      { t: "h2", text: "Organisation Impact Dashboard" },
      { t: "grid", columns: 3, items: [
        { title: "Participants",       body: "Total volunteers and members contributing through your programmes." },
        { title: "Volunteer hours",    body: "Verified time contributed across every project." },
        { title: "Verified activities",body: "Individual actions independently confirmed." },
        { title: "People helped",      body: "Aggregated headcount of beneficiaries." },
        { title: "Students mentored",  body: "One-to-one and group mentoring, tracked over time." },
        { title: "Environmental",      body: "Trees planted, hectares restored, species documented, food rescued." },
      ]},

      { t: "h2", text: "SocialCapital Connect API" },
      { t: "p", text: "Organisations do not need to abandon their existing technology. The SocialCapital Connect API can allow an approved organisation to report qualifying activities from its own system." },
      { t: "callout", text: "The organisation reports the facts. SocialCapital.cc evaluates the Social Capital. Partners can never arbitrarily manufacture points. This separation protects the integrity of the network." },

      { t: "h2", text: "Individual credit + organisational impact" },
      { t: "p", text: "The person receives recognition for the contribution. The organisation receives aggregated recognition for facilitating the impact. Everyone benefits from better documentation." },
    ],
    cta: { label: "Open the organisation dashboard", to: "/sc/org" },
  },

  // ────────────────────────────────────────────────────────────
  "challenges": {
    eyebrow: "Challenges",
    title: "Turn Good Intentions Into Collective Action",
    tagline:
      "People often want to help but do not know where to begin. Challenges provide clear goals.",
    blocks: [
      { t: "h2", text: "A Challenge can be simple" },
      { t: "grid", columns: 3, items: [
        { title: "Plant 10 Trees",              body: "Individual or family scale, verifiable with photos + geotag." },
        { title: "Volunteer 25 Hours",          body: "Roll-up across any participating organisation." },
        { title: "Mentor a Student",            body: "One-to-one relationship over a defined term." },
        { title: "Document 20 Bird Species",    body: "Citizen-science activity via NaturePulse." },
        { title: "Clean Your Street",           body: "Neighbourhood-scale community deed." },
        { title: "Reduce Food Waste 30 Days",   body: "Household habit change, logged daily." },
        { title: "Help Five Seniors",           body: "Transport, tech help, companionship." },
        { title: "Document Your Village",       body: "Local heritage & oral history collection." },
      ]},

      { t: "h2", text: "Who can create Challenges?" },
      { t: "ul", items: [
        "SocialCapital.cc",
        "Schools",
        "Universities",
        "Nonprofits",
        "Businesses",
        "Municipalities",
        "Environmental organisations",
        "Community groups",
        "Other verified partners",
      ]},

      { t: "h2", text: "Individual and team participation" },
      { t: "p", text: "Challenges can involve one person, families, classes, schools, companies, neighbourhoods, cities or global communities." },

      { t: "h2", text: "Document the result" },
      { t: "p", text: "Participants can record progress and provide evidence. Qualifying achievements may earn Social Capital, badges, certificates, milestones and community recognition." },

      { t: "h2", text: "Measure collective impact" },
      { t: "callout", text: "100 schools × 500 students × 10 trees = 500,000 trees. Social Capital helps connect individual action with collective impact." },
    ],
    cta: { label: "See collective impact", to: "/sc/impact" },
  },

  // ────────────────────────────────────────────────────────────
  "impact": {
    eyebrow: "Impact",
    title: "See the Good Happening Around Us",
    tagline:
      "The greatest story emerges when individual contributions are combined.",
    blocks: [
      { t: "p", text: "The SocialCapital.cc Impact platform aggregates appropriate, privacy-protected data to show how participating communities are contributing." },

      { t: "h2", text: "What can we measure?" },
      { t: "ul", items: [
        "Volunteer hours",
        "People helped",
        "Students mentored",
        "Trees planted",
        "Food rescued",
        "Community projects",
        "Environmental observations",
        "Educational contributions",
        "Citizen-science activities",
        "Many other indicators",
      ]},

      { t: "h2", text: "From neighbourhood to world" },
      { t: "p", text: "Impact information may be explored geographically:" },
      { t: "callout", text: "World → Country → State/Province → County/District → City/Village → Community → Organisation" },

      { t: "h3", text: "Portland" },
      { t: "grid", columns: 3, items: [
        { title: "42,800",  body: "Volunteer hours" },
        { title: "18,400",  body: "Trees planted" },
        { title: "1,250",   body: "Students mentored" },
        { title: "86,000",  body: "Pounds of food rescued" },
        { title: "14,700",  body: "Environmental observations" },
      ]},

      { t: "h3", text: "ABC High School" },
      { t: "grid", columns: 3, items: [
        { title: "620",   body: "Participating students" },
        { title: "8,420", body: "Service hours" },
        { title: "76",    body: "Community projects" },
        { title: "1,480", body: "Trees planted" },
      ]},

      { t: "h2", text: "Impact is more than a number" },
      { t: "callout", text: "Every number represents people. Every volunteer hour represents someone's time. Every tree represents an action. Every mentoring hour represents a human relationship. Every rescued meal represents food that did not go to waste." },
    ],
    cta: { label: "Read Stories", to: "/sc/stories" },
  },

  // ────────────────────────────────────────────────────────────
  "stories": {
    eyebrow: "Stories",
    title: "Behind Every Contribution Is a Story",
    tagline: "Numbers can measure impact. Stories give it meaning.",
    blocks: [
      { t: "p", text: "Social Capital Stories celebrates the people and communities behind documented contributions." },

      { t: "h2", text: "Ordinary people. Meaningful impact." },
      { t: "ul", items: [
        "A retired teacher mentoring students",
        "A child documenting neighbourhood birds",
        "A restaurant rescuing surplus meals",
        "An alumnus helping graduates find careers",
        "A farmer teaching sustainable techniques",
        "A neighbourhood restoring a park",
        "A photographer documenting environmental change",
        "A volunteer serving the same organisation for twenty years",
      ]},

      { t: "h2", text: "Tell your story" },
      { t: "p", text: "Members may connect stories with qualifying activities. Stories can include articles, photographs, videos, interviews, project histories and testimonials." },

      { t: "h2", text: "Organisation Stories" },
      { t: "p", text: "Schools, nonprofits and communities can tell collective stories. Instead of merely reporting “2,800 volunteer hours”, they can show the people and projects that created those hours." },

      { t: "h2", text: "Inspire without competing" },
      { t: "callout", text: "The purpose is inspiration. When people see what others accomplish, they may begin asking: What can I do? That may be the most valuable result of all." },
    ],
    cta: { label: "Start earning Social Capital", to: "/sc/earn" },
  },

  // ────────────────────────────────────────────────────────────
  "legacy": {
    eyebrow: "Legacy",
    title: "Your Social Capital Can Outlive Your Social Media",
    tagline:
      "The things we do for other people can matter for generations.",
    blocks: [
      { t: "p", text: "Social-media feeds are temporary by design. Yesterday's important post quickly becomes today's forgotten content. But the things we do for other people can matter for generations." },
      { t: "p", text: "SocialCapital.cc approaches contribution differently. We want meaningful actions to become part of a person's history." },

      { t: "h2", text: "Your lifetime timeline" },
      { t: "steps", items: [
        { n: 15,  label: "Age 15",  body: "School garden volunteer." },
        { n: 18,  label: "Age 18",  body: "Food-bank volunteer." },
        { n: 24,  label: "Age 24",  body: "Mentored university students." },
        { n: 37,  label: "Age 37",  body: "Community organisation leader." },
        { n: 52,  label: "Age 52",  body: "Environmental volunteer." },
        { n: 70,  label: "Age 70",  body: "Mentoring the next generation." },
      ]},
      { t: "p", text: "Individually, these may seem like small records. Together they tell the story of a life." },

      { t: "h2", text: "What do you want to be remembered for?" },
      { t: "p", text: "Members should eventually be able to curate a special Legacy section while they are alive. It may include:" },
      { t: "ul", items: [
        "Most meaningful contributions",
        "People mentored",
        "Organisations supported",
        "Projects created",
        "Knowledge shared",
        "Photographs",
        "Stories",
        "Awards",
        "Environmental contributions",
        "Personal reflections",
      ]},

      { t: "h2", text: "Legacy Profile" },
      { t: "p", text: "With appropriate instructions, permissions and family or estate controls, selected portions of a member's profile may eventually become a permanent Legacy Profile." },
      { t: "callout", text: "Instead of merely recording “Born — Died”, a legacy can also say: This is what this person contributed while they were here." },
    ],
    cta: { label: "Begin your record", to: "/sc/me" },
  },

  // ────────────────────────────────────────────────────────────
  "research": {
    eyebrow: "Research",
    title: "Understanding How Communities Become Stronger",
    tagline:
      "Bringing researchers and communities together to study what strengthens social fabric.",
    blocks: [
      { t: "p", text: "Social capital has important implications for education, economics, public health, community development, sociology, environmental science and public policy. SocialCapital.cc can help bring researchers and communities together." },

      { t: "h2", text: "Social Capital research areas" },
      { t: "ul", items: [
        "Trust",
        "Reciprocity",
        "Volunteerism",
        "Community participation",
        "Social networks",
        "Community resilience",
        "Social isolation",
        "Youth engagement",
        "Mentoring",
        "Citizen science",
        "Environmental stewardship",
        "Food security",
        "Community knowledge",
        "Digital reputation",
        "Social-impact measurement",
        "Responsible uses of artificial intelligence",
      ]},

      { t: "h2", text: "Social Capital Lab" },
      { t: "p", text: "A future Social Capital Lab could collaborate with universities, research institutes, schools, governments, nonprofits and community organisations." },
      { t: "p", text: "Researchers may explore questions such as:" },
      { t: "ul", items: [
        "Does youth volunteering encourage lifelong civic participation?",
        "Does mentoring improve educational outcomes?",
        "Can citizen science strengthen environmental awareness?",
        "What makes communities resilient?",
        "How can technology encourage positive participation without creating unhealthy social scoring?",
      ]},

      { t: "h2", text: "Privacy comes first" },
      { t: "callout", text: "Research access uses appropriate anonymisation, aggregation, consent and governance. SocialCapital.cc will never become a surveillance platform." },

      { t: "h2", text: "Research for action" },
      { t: "p", text: "Knowledge should ultimately help communities make better decisions. Research tells us what works. Communities put that knowledge into action. Social Capital can help document the result." },
    ],
    cta: { label: "About SocialCapital.cc", to: "/sc/about" },
  },

  // ────────────────────────────────────────────────────────────
  "about": {
    eyebrow: "About",
    title: "Why We Are Building SocialCapital.cc",
    tagline:
      "Human beings do enormous amounts of good that are never meaningfully documented.",
    blocks: [
      { t: "p", text: "People spend thousands of hours volunteering. Teachers mentor generations of students. Neighbours help neighbours. Parents support schools. Alumni guide young graduates. People plant trees. Communities preserve history. Volunteers rescue food. Citizens document nature. Professionals share knowledge." },
      { t: "callout", text: "Yet much of this contribution disappears. We believe technology can help preserve it." },

      { t: "h2", text: "Our mission" },
      { t: "p", text: "To create a trusted global infrastructure through which individuals and organisations can document, verify, measure, recognise, share and preserve positive contribution." },

      { t: "h2", text: "Powered by Social Pulse®" },
      { t: "p", text: "SocialCapital.cc works as the reputation, impact and legacy layer of the broader Social Pulse ecosystem. Participating platforms may document activities involving community, education, nature, food, agriculture, travel, family heritage, local information and many other areas. Qualifying activities can contribute to one unified Social Capital record." },

      { t: "h2", text: "Open to organisations beyond Social Pulse" },
      { t: "p", text: "Through the SocialCapital Connect API, approved schools, nonprofits, businesses, universities, municipalities and other organisations can connect their existing systems to the Social Capital network." },

      { t: "h2", text: "Our principles" },
      { t: "grid", columns: 2, items: [
        { title: "Contribution over popularity",  body: "We reward what you do, not how loud you are." },
        { title: "Evidence over unsupported claims", body: "Verification levels weight recognition honestly." },
        { title: "Recognition without judgment",  body: "Social Capital does not rank human worth." },
        { title: "Privacy by design",             body: "Members choose what appears in each Passport." },
        { title: "Protection for children",       body: "Minor profiles receive enhanced safeguards." },
        { title: "Transparency and accountability", body: "The scoring formula is public. The ledger is auditable." },
        { title: "One person, one portable record", body: "Contributions follow you across organisations." },
        { title: "Impossible to buy",             body: "Social Capital cannot simply be purchased." },
      ]},

      { t: "h2", text: "Our vision" },
      { t: "p", text: "Imagine a child beginning to document community service at school. Years later, that record includes university activities, professional mentoring, environmental work, volunteering, community leadership and decades of knowledge sharing." },
      { t: "callout", text: "At age 70 or 80, that person may possess something no traditional social network has provided: a structured history of the positive impact of an entire life." },
      { t: "p", text: "Someday, instead of disappearing with an inactive account, selected contributions can remain as a legacy for children, grandchildren, communities and future generations." },

      { t: "divider" },
      { t: "callout", text: "Every Good Deed Counts. Every Contribution Tells a Story. Every Life Can Leave a Legacy." },
    ],
    cta: { label: "Start your record", to: "/sc/me" },
  },

  // ────────────────────────────────────────────────────────────
  "api": {
    eyebrow: "API / Partners",
    title: "Bring Your Organisation Onto the Ledger",
    tagline:
      "SocialCapital Connect lets your existing systems submit verified activities without abandoning your workflows.",
    blocks: [
      { t: "p", text: "SocialCapital.cc is designed to work with the systems your organisation already uses — from student information systems and volunteer management tools to CRM and time-tracking platforms." },

      { t: "h2", text: "Two authentication flows" },
      { t: "grid", columns: 2, items: [
        { title: "X-API-Key",       body: "The simplest path. Your super-admin mints one key per environment (sandbox / production). Send it on every request. Ideal for small nonprofits, pilots and internal tooling." },
        { title: "OAuth2 client_credentials", body: "Standards-compliant flow for enterprise deployments. Exchange a client_id + client_secret for a 1-hour Bearer token with scope-aware access to the API." },
      ]},

      { t: "h2", text: "One canonical submit endpoint" },
      { t: "p", text: "You report facts about an activity — category, hours or units, evidence, verification claim, participant. The engine calculates recognition. Idempotency is guaranteed: retries never double-credit." },

      { t: "h2", text: "Public ledger reads" },
      { t: "p", text: "Third parties can independently verify a member's Social Capital record without any credentials. Every ledger row is SHA-256 hash-chained; anyone can walk the chain and confirm integrity." },

      { t: "h2", text: "Who can integrate?" },
      { t: "ul", items: [
        "Schools and school districts",
        "Universities and colleges",
        "Nonprofits and NGOs",
        "Charities and food banks",
        "Municipalities and government agencies",
        "Volunteer-management platforms",
        "Corporate CSR programmes",
        "Alumni associations",
        "Environmental and citizen-science networks",
      ]},

      { t: "h2", text: "Getting started" },
      { t: "steps", items: [
        { n: 1, label: "Register your organisation",    body: "Submit your organisation's name, contact and country to a SocialCapital.cc super-admin, or ask an existing partner to sponsor you." },
        { n: 2, label: "Mint sandbox credentials",      body: "Receive one API key (or a client_id + secret) for the sandbox environment. Copy the raw value immediately — we hash it at rest." },
        { n: 3, label: "Submit test activities",        body: "Use the sandbox to submit a few real historical activities. Verify the returned scoring breakdown and passport update." },
        { n: 4, label: "Request production credentials", body: "Once your integration is stable, request a production key. Your trust level determines your recognition multiplier." },
      ]},

      { t: "callout", text: "The organisation reports the facts. SocialCapital.cc evaluates the Social Capital. This separation protects the integrity of the ledger for everyone." },
    ],
    cta: { label: "Read the API reference", to: "/sc/docs" },
  },
};

export function getArticle(slug) {
  return articles[slug] || null;
}
